/* ProfileViewsPanel — لَوحة مُشاهَدات ملفّ المحامي العامّ.
   تُظهِر الإحصاءات الأسبوعيّة + مُخَطَّط مَصغَّر لآخر 7 أيّام.
   تَستخدم endpoint موجود سَلفاً: GET /api/lawyers/me/views */
import { useEffect, useState } from "react";
import { Eye, ChartLineUp, ShareNetwork } from "@phosphor-icons/react";
import { getMyViewStats } from "../../lib/api";

const DAY_LABELS = ["أحد", "اثنين", "ثلاثاء", "أربعاء", "خميس", "جمعة", "سبت"];

function dayShortLabel(isoDay) {
  // isoDay: "YYYY-MM-DD" — نَأخذ يوم الأُسبوع
  try {
    const d = new Date(isoDay + "T00:00:00");
    return DAY_LABELS[d.getDay()];
  } catch {
    return "";
  }
}

export default function ProfileViewsPanel({ slug }) {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    getMyViewStats()
      .then((d) => { if (alive) setStats(d); })
      .catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, []);

  if (err || !stats) return null;

  const week = stats.this_week || 0;
  const month = stats.this_month || 0;
  const total = stats.total || 0;
  const series = stats.last_7_days || [];
  const max = Math.max(1, ...series.map((s) => s.count || 0));

  // الحالة الفارِغة — تَحفيز المُشارَكة
  if (total === 0) {
    return (
      <div
        data-testid="profile-views-panel-empty"
        className="mt-3 rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-l from-[#FFFEF7] to-[#FFF9E6] p-3.5 flex items-start gap-2.5"
      >
        <div className="w-9 h-9 rounded-full bg-[#D4AF37]/15 flex items-center justify-center shrink-0">
          <ShareNetwork size={17} weight="duotone" className="text-[#B8941F]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[12.5px] font-bold text-[#0A192F] mb-0.5">
            ابدَأ بِناء حُضورك
          </div>
          <p className="text-[11.5px] text-[#5A6470] leading-relaxed">
            شارك بِطاقَتك مع موكِّليك على واتساب لِتَبدأ المُشاهَدات بالتَّوافُد.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="profile-views-panel"
      className="mt-3 rounded-2xl border border-[#0A192F]/10 bg-gradient-to-br from-[#0A192F] via-[#14213D] to-[#1A2F50] p-4 text-white overflow-hidden relative"
    >
      {/* لمعة ذَهبيّة في الزّاوية */}
      <div
        aria-hidden="true"
        className="absolute -top-8 -left-8 w-32 h-32 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(212,175,55,0.25) 0%, rgba(212,175,55,0) 70%)" }}
      />

      <div className="relative flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 text-[10.5px] text-[#FFE7A6] font-bold uppercase tracking-wider mb-1">
            <ChartLineUp size={12} weight="fill" />
            نَشاط ملفّك العامّ
          </div>
          <div className="text-[13px] text-white/85 leading-snug">
            شاهَدَ ملفّك
            <span
              className="mx-1.5 inline-block text-[22px] font-extrabold tabular-nums text-[#FFE7A6] align-middle"
              data-testid="views-week-count"
            >
              {week}
            </span>
            {week === 1 ? "مَرّة" : week <= 10 ? "مَرّات" : "مَرّة"} هذا الأُسبوع
          </div>
          <div className="mt-1 text-[11px] text-white/55">
            <span className="tabular-nums font-bold text-white/75" data-testid="views-month-count">{month}</span> هذا الشَّهر ·
            <span className="tabular-nums font-bold text-white/75 mx-1" data-testid="views-total-count">{total}</span>
            إجماليّاً
          </div>
        </div>
        <div className="w-9 h-9 rounded-full bg-white/10 border border-white/20 flex items-center justify-center shrink-0">
          <Eye size={17} weight="fill" className="text-[#FFE7A6]" />
        </div>
      </div>

      {/* مُخَطَّط أعمِدة لآخر 7 أيّام */}
      {series.length > 0 && (
        <div
          className="relative mt-3 flex items-end gap-1.5 h-14"
          data-testid="views-sparkline"
          aria-label={`مُخطَّط مُشاهَدات آخر ${series.length} أيّام`}
        >
          {series.map((s, idx) => {
            const h = max > 0 ? Math.max(4, Math.round((s.count / max) * 100)) : 4;
            const isToday = idx === series.length - 1;
            return (
              <div key={s.day} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                <div className="w-full flex items-end h-10">
                  <div
                    className="w-full rounded-t-md transition-all"
                    style={{
                      height: `${h}%`,
                      background: isToday
                        ? "linear-gradient(180deg, #FFE7A6 0%, #D4AF37 100%)"
                        : "rgba(255,231,166,0.40)",
                      boxShadow: isToday ? "0 0 12px rgba(212,175,55,0.45)" : "none",
                    }}
                    title={`${s.count} مُشاهَدة`}
                  />
                </div>
                <span className="text-[9px] text-white/55 truncate">
                  {dayShortLabel(s.day)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
